import classnames from 'classnames'

import { PageItems, TablePaginationProps } from '../../hooks/use-table-pagination'
import { Button, ButtonComponentProps, ButtonProps } from '../button'

interface PaginationProps<TData, TButton extends React.ElementType>
  extends Omit<TablePaginationProps<TData>, 'visiblePages' | 'paginatedData' | 'pageCount'>,
    ButtonProps {
  visiblePages: Array<number | PageItems.gap>
  getPageItemButtonProps?: (page: number, pageItem: PageItems) => ButtonComponentProps<TButton>
  className?: string
}

export function Pagination<TData, TagType extends React.ElementType>({
  canGoBack,
  goBack,
  canGoForward,
  goForward,
  canGoToPage,
  goToPage,
  visiblePages,
  currentPage,
  getPageItemButtonProps,
  className,
  ...buttonProps
}: PaginationProps<TData, TagType>) {
  return (
    <div className={classnames('flex gap-1', className)}>
      <div className="flex w-full justify-end">
        {canGoBack && (
          <Button
            title="Go to previous page"
            onClick={goBack}
            rightContent="arrowBackward"
            theme="chipTransparent"
            size="small"
            {...buttonProps}
            {...(getPageItemButtonProps?.(currentPage - 1, PageItems.prev) as ButtonComponentProps<TagType>)}
          />
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {visiblePages.map((pageNumber) =>
          pageNumber === PageItems.gap ? (
            <span className="text-gray-200 font-bold dark:text-gray-600">⋯</span>
          ) : (
            <Button
              key={pageNumber}
              className="min-w-7"
              title={`Go to page ${pageNumber}`}
              disabled={!canGoToPage(pageNumber)}
              onClick={() => goToPage(pageNumber)}
              active={currentPage === pageNumber}
              theme="chipTransparent"
              size="small"
              {...buttonProps}
              {...(getPageItemButtonProps?.(pageNumber, PageItems.pageNumber) as ButtonComponentProps<TagType>)}
            >
              {pageNumber}
            </Button>
          )
        )}
      </div>
      <div className="w-full">
        {canGoForward && (
          <Button
            title="Go to next page"
            onClick={goForward}
            rightContent="arrowForward"
            theme="chipTransparent"
            size="small"
            {...buttonProps}
            {...(getPageItemButtonProps?.(currentPage + 1, PageItems.next) as ButtonComponentProps<TagType>)}
          />
        )}
      </div>
    </div>
  )
}
